// Type-the-name flow. Calls /api/medications/search with a debounced
// query, surfaces RxNorm candidates, and on confirm POSTs to
// /api/medications with the chosen RxCUI. Same end-state as the photo
// flow (lands on My medicines).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type AllergyPrecheckMatch, useAllergyPrecheck } from '../api/allergies';
import type { OcrCandidate } from '../api/ocr';
import { AllergyAlertPanel } from '../components/AllergyAlertPanel';
import { SeniorBanner } from '../components/senior/SeniorBanner';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { AppPage } from '../layouts/AppPage';

interface SearchResponse {
  ok: boolean;
  data?: { candidates: OcrCandidate[] };
  error?: { message: string };
}

interface AddResponse {
  ok: boolean;
  data?: { id?: string };
  error?: { message?: string };
}

export function AddByName(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const navigate = useNavigate();
  const precheck = useAllergyPrecheck();
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<OcrCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  // When the precheck returns matches, we render AllergyAlertPanel and
  // hold the pending candidate. The user must either cancel or
  // explicitly type "I understand" to proceed (see AllergyAlertPanel).
  const [pendingAllergy, setPendingAllergy] = useState<{
    candidate: OcrCandidate;
    matches: AllergyPrecheckMatch[];
  } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus the input on mount so the cursor is ready to type.
  useEffect(() => {
    const isTouch =
      typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    if (!isTouch) inputRef.current?.focus();
  }, []);

  // Debounced search. Cancel in-flight requests when the user keeps
  // typing so a slow earlier request can't clobber a faster later one.
  const trimmed = useMemo(() => query.trim(), [query]);
  useEffect(() => {
    if (trimmed.length < 2) {
      setCandidates([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const res = await authedFetch(
          `/api/medications/search?q=${encodeURIComponent(trimmed)}&limit=5`,
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const body = (await res.json()) as SearchResponse;
        if (!body.ok) throw new Error(body.error?.message ?? `HTTP ${res.status}`);
        setCandidates(body.data?.candidates ?? []);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 300);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [trimmed, authedFetch]);

  async function pick(c: OcrCandidate) {
    setSubmitting(c.rxcui);
    setError(null);
    try {
      // Pre-add allergy cross-reactivity check. Round-4 #1 + #5.
      // If the user has any allergy that cross-reacts with this
      // ingredient / class, render a CRITICAL takeover modal and gate
      // the save behind explicit double-confirmation.
      const matches = await precheck({
        ingredientName: c.ingredientName ?? undefined,
        displayName: c.displayName,
        drugClasses: [],
      }).catch(() => [] as AllergyPrecheckMatch[]);
      if (matches.length > 0) {
        setSubmitting(null);
        setPendingAllergy({ candidate: c, matches });
        return;
      }
      await saveMedication(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(null);
    }
  }

  async function saveMedication(c: OcrCandidate): Promise<void> {
    setSubmitting(c.rxcui);
    setError(null);
    try {
      const res = await authedFetch('/api/medications', {
        method: 'POST',
        body: JSON.stringify({
          name: c.displayName,
          frequency: 'as prescribed',
          rxcui: c.rxcui.startsWith('synthetic-') ? undefined : c.rxcui,
          addedVia: 'NAME_SEARCH',
        }),
      });
      const body = (await res.json()) as AddResponse;
      if (!body.ok) throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      navigate('/medications', { state: { celebrate: 'med-added' } });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <AppPage title="Type the medicine name" back={{ to: '/medications', label: 'Medications' }}>
      <section
        style={{
          background: 'var(--color-surface-primary)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-md)',
        }}
      >
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: 0 }}>
          Start typing the medicine name. We will look up the exact match in the FDA database and
          show you a few options to confirm.
        </p>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Medicine name</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. Lipitor, metformin, vitamin D"
            autoComplete="off"
            spellCheck={false}
            aria-label="Medicine name"
            style={{
              padding: '14px 16px',
              fontSize: 18,
              minHeight: 48,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
            }}
          />
        </label>

        {error ? (
          <SeniorBanner title="Could not search for that medicine" tone="critical">
            {error}
          </SeniorBanner>
        ) : null}

        {trimmed.length >= 2 && searching && candidates.length === 0 ? (
          <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', margin: 0 }}>
            Looking up...
          </p>
        ) : null}

        {trimmed.length >= 2 && !searching && candidates.length === 0 && !error ? (
          <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', margin: 0 }}>
            No matches yet. Try a different spelling, or take a photo of the bottle instead.
          </p>
        ) : null}

        {candidates.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>
              Tap the option that matches your bottle.
            </p>
            {candidates.map((c) => (
              <SeniorButton
                key={c.rxcui}
                onClick={() => void pick(c)}
                fullWidth
                size="large"
                disabled={submitting !== null}
                data-testid={`name-candidate-${c.rxcui}`}
              >
                {c.displayName}
              </SeniorButton>
            ))}
          </div>
        ) : null}

        <SeniorButton
          onClick={() => navigate('/medications/add-by-photo')}
          fullWidth
          data-testid="back-to-photo"
        >
          Take a photo of the bottle instead
        </SeniorButton>
      </section>
      {pendingAllergy ? (
        <AllergyAlertPanel
          matches={pendingAllergy.matches}
          medicationName={pendingAllergy.candidate.displayName}
          onCancel={() => setPendingAllergy(null)}
          onConfirmAddAnyway={() => {
            const c = pendingAllergy.candidate;
            setPendingAllergy(null);
            void saveMedication(c);
          }}
        />
      ) : null}
    </AppPage>
  );
}
